import get from 'lodash/get';
import { controlnetMap, initialGeneratorParams } from '@shakkerai/pro-constants';
import { findNextTaskPosition, submitGeneration, resumeGeneration } from '../helpers/generate';
import { viewportActions, updateQueue, updateTask, setSharedData, setProjectState } from '@starflow/app/store';
import { enterDerivativeFromUnit } from '../../derivative';
import { translate } from '@shakkerai/util-i18n';

// 流节点的能力描述
// 1. 包含输入参数和输出参数，并且输入和输出的端口都是动态的
// 2. 包含执行函数，用于执行功能模块的功能
// 3. 可以注册新的画布元素
// 4. 可以在应用的各个预设位置渲染自定义的UI组件
// 5. 可以暴露出一些方法供其他功能模块调用
// 6. 可以在应用初始化时进行一些初始化操作
// 7. 有一些生命周期函数，用于在特定的时机执行一些操作

const baseInputPorts = [
  {
    name: 'imageReference',
    type: 'image',
    label: '图生图',
    validato: (value, { module, app }) => {
      return true;
    },
  },
];

const baseOutputPorts = [
  {
    name: 'items',
    type: 'images',
  },
];

const GENERATOR_ITEMS_GAP = 32;

export default {
  type: 'generator', // 节点类型，generator: 标准生图任务节点, custom: 自定义节点

  title: translate('flow_g_title'),

  isFlowUnit: true, // 是否是流程单元

  defaultData: initialGeneratorParams.primary, // 默认数据

  extractProjectData: (data) => {
    // 旧数据兼容
    if (data.sourceImage && !data.sourceElement) {
      data.sourceElement = data.sourceImage;
    }

    return data;
  },

  methods: {},

  // 获取输入端口
  getInputPorts(data) {
    if (!data || !data.controlnets) {
      return baseInputPorts;
    }

    return baseInputPorts.concat(
      data.controlnets.map((item, index) => {
        return {
          type: 'image',
          name: `controlnets.${index}.sourceImage`,
          label: controlnetMap[item.type]?.label || item.type,
        };
      }),
    );
  },

  // 获取输出端口
  getOutputPorts(data) {
    return baseOutputPorts;
  },

  // 设置输入
  applyInput(name, value, data) {
    if (name.startsWith('controlnets.')) {
      const [, cnIndex, fieldName] = name.split('.');

      return {
        ...data,
        controlnets: data.controlnets.map((item, index) => {
          if (index !== Number(cnIndex)) return item;
          return {
            ...item,
            [fieldName]: value,
          };
        }),
      };
    } else {
      return { ...data, [name]: { ...data[name], ...value } };
    }
  },

  // 获取输出
  getInputs(data, name) {
    if (!name) {
      const inputPorts = this.getInputPorts(data);

      return inputPorts.map((port) => ({
        name: port.name,
        type: port.type,
        value: get(data, port.name),
        label: port.label,
      }));
    }

    return get(data, name);
  },

  // 获取输出
  getOutput(name, data) {
    return name ? data.result[name] : data.result;
  },

  onNodeClick(data) {
    // 如果是流上的生成器，则进入画布
    if (data.flowId) {
      enterDerivativeFromUnit('generator', data);
    } else {
      setProjectState({
        selection: [],
        currentElementTool: null,
        currentNodeId: data.id,
      });
    }
  },

  onDataUpdate(data, app) {},

  onFocus(data, app) {},

  // 主生成器任务在插入到队列之后执行的操作
  onQueue({ data }) {
    const { x, y } = findNextTaskPosition();
    const { width, height, count } = data.params;
    const rectWidth = width * count + (count - 1) * GENERATOR_ITEMS_GAP;

    console.log(`[DEBUG] { x, y, width, height }`, { x, y, width, height });

    updateQueue(data.id, { x, y, width, height });

    localStorage.setItem(`lastGeneratorRect`, JSON.stringify({ x, y, width: rectWidth, height }));

    // 如果是在flow中手动发起的生成器，则不自动定位
    if (!data.isFlowManualGenerator) {
      viewportActions.locateRect(
        { x, y, width: rectWidth, height },
        { paddings: [100, 100, 200, 100], animate: true, fitView: true },
      );
    }
  },

  onBeforeExecute(data, app) {},

  onAfterExecute(data, app) {},

  async resume({ data }) {
    await resumeGeneration(data);
  },

  // 主生成器任务的具体执行函数，该任务执行完则会执行下一个任务
  async execute({ data }) {
    const { width, height, count } = data.params;
    const taskPositionInfo = {
      x: data.x,
      y: data.y,
      width,
      height,
    };

    if (typeof data.x === 'undefined' || typeof data.y === 'undefined') {
      // 执行之前，在画布上寻找合适的显示生成器
      const { x, y } = findNextTaskPosition();
      const rectWidth = width * count + (count - 1) * GENERATOR_ITEMS_GAP;

      taskPositionInfo.x = x;
      taskPositionInfo.y = y;

      updateTask(data.id, taskPositionInfo);

      localStorage.setItem(`lastGeneratorRect`, JSON.stringify({ x, y, width: rectWidth, height }));

      viewportActions.locateRect(
        { x, y, width: rectWidth, height },
        { paddings: [100, 100, 200, 100], animate: true, fitView: true },
      );
    }

    // 临时把生成器坐标存下来，下个节点继续用
    if (data.isAuto) {
      data.params.count = 1;
      setSharedData('autoRunningPositionInfo', taskPositionInfo);
    }

    await submitGeneration(data);
  },
};
